import {
  IconCircleInfo,
  Inspector,
  GreyGrey70,
  GreyWhite,
  Flex
} from '@sequencehq/core-components'
import { useQuoteEditorSidebar } from '../../QuoteEditorSidebar/useQuoteEditorSidebar'
import { SidebarSection } from '../../QuoteEditorSidebar/widgets/SidebarSection'
import { PublishedStatus } from '../../QuoteEditorSidebar/PublishedStatus'
import { SidebarFields } from '../../QuoteEditorSidebar/widgets/SidebarFields'
import { ContractValue } from '../../QuoteEditorSidebar/widgets/ContractValue'
import { PublishedStatusV2 } from '../../QuoteEditorSidebar/widgets/PublishedStatusV2'
import { useEmbeddedSigningFlow } from '../../hooks'
import {
  QUOTE_RECIPIENTS_COPY,
  QuoteRecipients
} from 'Cube/view/layouts/quote/components/QuoteRecipients'
import { useCubeContext } from 'Cube/communication/internal/cube.domain.context'
import { match } from 'ts-pattern'

const ItemContent = () => {
  const hook = useQuoteEditorSidebar()
  const { isEnabled: isEmbeddedSigningEnabled } = useEmbeddedSigningFlow()
  const contractValueTitle = useContractValueTitle()

  // The section hides itself on a quote with nobody to list, and the section
  // above it only draws its border when something follows it.
  const isRecipientsSectionVisible = hook.features.isRecipientsVisible

  return (
    <Flex
      backgroundColor={GreyWhite}
      height="100%"
      flexDirection="column"
      width="100%"
      gap="16px"
    >
      {isEmbeddedSigningEnabled && <PublishedStatusV2 />}

      <Flex
        flexDirection="column"
        width="100%"
        gap="16px"
        paddingX="8px"
        paddingY={isEmbeddedSigningEnabled ? '0px' : '8px'}
      >
        {!isEmbeddedSigningEnabled && (
          <SidebarSection
            title="Status"
            showBorder
            data-testid="quote.sidebarSection.status"
          >
            <PublishedStatus />
          </SidebarSection>
        )}

        <SidebarSection
          title={contractValueTitle}
          showBorder
          data-testid="quote.sidebarSection.contractValue"
        >
          <ContractValue />
        </SidebarSection>

        <SidebarSection
          title="Details"
          showBorder={isRecipientsSectionVisible}
          data-testid="quote.sidebarSection.details"
        >
          <SidebarFields hook={hook} />
        </SidebarSection>

        {isRecipientsSectionVisible && (
          <SidebarSection
            title={QUOTE_RECIPIENTS_COPY.section}
            data-testid="quote.sidebarSection.recipients"
          >
            <QuoteRecipients />
          </SidebarSection>
        )}
      </Flex>
    </Flex>
  )
}

export const QuoteInformationInspectorItem: React.FC = () => {
  return (
    <Inspector.IconItem
      data-testid="quote-inspector-information"
      tooltip="Quote information"
      content={
        <Inspector.Content
          padding="8px"
          data-testid="quote.inspector.information"
        >
          <ItemContent />
        </Inspector.Content>
      }
      icon={<IconCircleInfo color={GreyGrey70} />}
    />
  )
}

/**
 * An amendment quote carries the whole amended schedule, so the total under this
 * heading is what the contract will be worth once the amendment takes effect,
 * not the amount the amendment adds. "New contract value" says that, where plain
 * "Contract value" reads like the existing contract's.
 */
const useContractValueTitle = (): string => {
  const ctx = useCubeContext()

  return match(ctx.queries.rawData.data.quote.dealType)
    .with('AMENDMENT', () => 'New contract value')
    .with('NEW_BUSINESS', 'RENEWAL', () => 'Contract value')
    .exhaustive()
}
